import React from 'react';
import { Check, UserCheck } from 'lucide-react';

const ModelBuilderBadge = ({
  employeeId,
  employeeName,
  isEligible,
  isManuallyAssigned,
  onClick,
  userCanAssign,
  assignedByName
}) => {
  // Determine badge state and styling
  const getFirstName = (name) => name?.split(' ')[0] || name;
  const firstName = getFirstName(employeeName);

  // Badge colors per model builder
  const getBuilderColor = (name) => {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('adam')) return { base: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)', border: 'rgba(59, 130, 246, 0.3)' };
    if (lowerName.includes('zach')) return { base: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.1)', border: 'rgba(139, 92, 246, 0.3)' };
    if (lowerName.includes('stef')) return { base: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)', border: 'rgba(245, 158, 11, 0.3)' };
    // Default color
    return { base: '#6b7280', bg: 'rgba(107, 114, 128, 0.1)', border: 'rgba(107, 114, 128, 0.3)' };
  };

  const colors = getBuilderColor(employeeName);

  // State-based styling
  let badgeColor, backgroundColor, borderColor, opacity, cursor, icon;

  if (isManuallyAssigned) {
    // Manually assigned - green with user check icon
    badgeColor = 'var(--success)';
    backgroundColor = 'rgba(34, 197, 94, 0.1)';
    borderColor = 'rgba(34, 197, 94, 0.3)';
    opacity = 1;
    cursor = userCanAssign ? 'pointer' : 'default';
    icon = <UserCheck size={12} />;
  } else if (isEligible) {
    // Eligible by rules - green with checkmark
    badgeColor = 'var(--success)';
    backgroundColor = 'rgba(34, 197, 94, 0.1)';
    borderColor = 'rgba(34, 197, 94, 0.3)';
    opacity = 1;
    cursor = userCanAssign ? 'pointer' : 'default';
    icon = <Check size={12} />;
  } else {
    // Not eligible - gray/faded
    badgeColor = 'var(--gray-500)';
    backgroundColor = 'var(--gray-800)';
    borderColor = 'var(--gray-700)';
    opacity = 0.6;
    cursor = userCanAssign ? 'pointer' : 'not-allowed';
    icon = null;
  }

  // Tooltip text
  const getTooltipText = () => {
    if (isManuallyAssigned) {
      return assignedByName
        ? `Manually assigned by ${assignedByName}`
        : 'Manually assigned';
    }
    if (isEligible) {
      return 'Eligible by assignment rules';
    }
    return 'Not eligible for this project';
  };

  const handleClick = (e) => {
    e.stopPropagation(); // Prevent card click
    if (userCanAssign && onClick) {
      onClick(employeeId);
    }
  };

  return (
    <div
      onClick={handleClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-1)',
        padding: '4px 8px',
        borderRadius: 'var(--radius-md)',
        fontSize: 'var(--text-xs)',
        fontWeight: 'var(--font-medium)',
        color: badgeColor,
        backgroundColor,
        border: `1px solid ${borderColor}`,
        opacity,
        cursor,
        transition: 'all var(--transition-fast)',
        userSelect: 'none',
      }}
      onMouseEnter={(e) => {
        if (userCanAssign) {
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
        }
      }}
      onMouseLeave={(e) => {
        if (userCanAssign) {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }
      }}
      title={getTooltipText()}
    >
      {icon}
      <span>{firstName}</span>
    </div>
  );
};

export default ModelBuilderBadge;
